<template>
  <div v-if="!isPermanentlyDismissed && onboarding" class="mb-8">
    <!-- ══ CONTENEDOR PRINCIPAL DE LA GUÍA ══ -->
    <div 
      class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-orange-50/30 to-amber-50/40 p-6 sm:p-8 border border-orange-200/90 shadow-sm transition-all duration-300 hover:shadow-md"
    >
      <!-- Decoración de fondo suave -->
      <div class="pointer-events-none absolute -right-16 -top-16 w-56 h-56 bg-orange-200/30 rounded-full blur-3xl" />
      <div class="pointer-events-none absolute -left-12 -bottom-12 w-48 h-48 bg-amber-200/20 rounded-full blur-2xl" />

      <!-- Cabecera de la Guía -->
      <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div class="flex items-center gap-2 mb-1.5 flex-wrap">
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-xs font-bold uppercase tracking-wider">
              <i class="fas fa-rocket text-orange-600" />
              Guía de Inicio Rápido
            </span>
            <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white border border-orange-200 text-orange-700 text-xs font-semibold">
              <i class="fas fa-store text-[10px]" />
              {{ onboarding.business_type_label }}
            </span>
            <span v-if="onboarding.is_completed" class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
              <i class="fas fa-check-circle text-emerald-600" />
              ¡100% Completado!
            </span>
          </div>

          <h2 class="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
            {{ onboarding.is_completed ? '🎉 ¡Tu negocio está listo para operar!' : 'Configura tu plataforma paso a paso' }}
          </h2>
          <p class="text-xs sm:text-sm text-gray-600 mt-0.5">
            {{ onboarding.is_completed 
              ? 'Has completado los pasos clave del onboarding inicial. Ahora puedes aprovechar todo el potencial de Realgestión.' 
              : 'Sigue estos 4 sencillos pasos para empezar a vender, controlar inventario y emitir comprobantes.' 
            }}
          </p>
        </div>

        <!-- Controles de vista (Minimizar / Ocultar) -->
        <div class="flex items-center gap-2 self-start md:self-auto">
          <button 
            type="button" 
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 text-xs font-semibold border border-gray-200 shadow-sm transition-all"
            :title="isCollapsed ? 'Desplegar tareas' : 'Minimizar tareas'"
            @click="isCollapsed = !isCollapsed"
          >
            <i :class="isCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'" class="text-[10px]" />
            <span>{{ isCollapsed ? 'Ver tareas (' + onboarding.completed_count + '/4)' : 'Minimizar' }}</span>
          </button>

          <button 
            v-if="onboarding.is_completed"
            type="button" 
            class="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 transition-all"
            title="Ocultar guía del panel"
            @click="dismissPermanently"
          >
            <i class="fas fa-times text-[10px]" />
            <span>Cerrar guía</span>
          </button>
        </div>
      </div>

      <!-- Barra de Progreso -->
      <div class="relative z-10 mb-6 bg-white/70 backdrop-blur-sm p-4 rounded-2xl border border-orange-100 shadow-xs">
        <div class="flex items-center justify-between text-xs font-bold mb-2">
          <span class="text-gray-700 flex items-center gap-1.5">
            <i class="fas fa-tasks text-orange-500" />
            Progreso del Onboarding
          </span>
          <span class="text-orange-600">
            {{ onboarding.percentage }}% ({{ onboarding.completed_count }} de {{ onboarding.total_count }} completados)
          </span>
        </div>
        <div class="w-full bg-gray-200/80 rounded-full h-3 overflow-hidden p-0.5">
          <div 
            class="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-700 ease-out shadow-xs"
            :style="{ width: onboarding.percentage + '%' }"
          />
        </div>
      </div>

      <!-- LISTA DE TAREAS (Colapsable) -->
      <div v-show="!isCollapsed" class="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          v-for="(step, idx) in onboarding.steps" 
          :key="step.id"
          class="rounded-2xl p-5 border flex flex-col justify-between transition-all duration-200 relative group bg-white shadow-xs"
          :class="step.completed 
            ? 'border-emerald-200/80 bg-emerald-50/20' 
            : 'border-orange-200/80 hover:border-orange-400 hover:shadow-md'"
        >
          <!-- Badge de Estado Superior -->
          <div class="flex items-center justify-between mb-3">
            <span class="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
              Paso {{ idx + 1 }}
            </span>
            <span 
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
              :class="step.completed ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'"
            >
              <i :class="step.completed ? 'fas fa-check-circle text-emerald-600' : 'fas fa-clock text-orange-600'" />
              {{ step.completed ? 'Completado' : 'Pendiente' }}
            </span>
          </div>

          <!-- Icono y Título -->
          <div class="mb-4">
            <div class="flex items-center gap-3 mb-2">
              <div 
                class="w-10 h-10 rounded-xl flex items-center justify-center text-base"
                :class="step.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-600'"
              >
                <i :class="step.icon" />
              </div>
              <h3 class="font-bold text-gray-900 text-sm leading-tight flex-1">
                {{ step.title }}
              </h3>
            </div>
            <p class="text-xs text-gray-600 leading-relaxed min-h-[36px]">
              {{ step.description }}
            </p>
          </div>

          <!-- Botones de Acción y Tutorial -->
          <div class="pt-3 border-t border-gray-100 flex flex-col gap-2">
            <!-- Botón Principal -->
            <Link 
              v-if="step.action_url"
              :href="step.action_url"
              class="w-full inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl font-bold text-xs transition-all text-center"
              :class="step.completed 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-orange-600 hover:bg-orange-700 text-white shadow-sm hover:shadow'"
            >
              <span>{{ step.action_label }}</span>
              <i class="fas fa-arrow-right text-[10px]" />
            </Link>

            <!-- Botón Secundario (Ej: Registrar Compra o Nueva Venta) -->
            <Link 
              v-if="step.secondary_url && !step.completed"
              :href="step.secondary_url"
              class="w-full inline-flex items-center justify-center gap-1 py-1.5 px-3 rounded-xl font-semibold text-xs text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition-all text-center"
            >
              <span>{{ step.secondary_label }}</span>
            </Link>

            <!-- Botón de Tutorial Rápido -->
            <button 
              v-if="step.tutorial_id"
              type="button"
              class="w-full inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-xl text-[11px] font-semibold text-orange-700 hover:text-orange-900 hover:bg-orange-50 transition-colors"
              @click="openTutorial(step.tutorial_id)"
            >
              <i class="fas fa-lightbulb text-orange-500 text-xs" />
              <span>¿Cómo funciona? Tutorial</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL DE TUTORIAL INTERACTIVO ══ -->
    <teleport to="body">
      <div v-if="showTutorialModal" class="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" @click="showTutorialModal = false" />

        <!-- Modal Box -->
        <div class="relative w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl z-10 animate-in fade-in zoom-in duration-200">
          <!-- Encabezado con degradado naranja -->
          <div class="bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 p-6 text-white relative">
            <button 
              class="absolute top-5 right-5 w-8 h-8 rounded-full bg-black/20 hover:bg-black/30 text-white flex items-center justify-center transition-all" 
              @click="showTutorialModal = false"
            >
              <i class="fas fa-times text-sm" />
            </button>

            <div class="flex items-center gap-3 mb-2">
              <span class="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-sm">
                <i class="fas fa-graduation-cap" />
              </span>
              <span class="text-xs font-bold uppercase tracking-wider text-orange-100">Guía de Inicio Rápido</span>
            </div>
            <h3 class="text-xl sm:text-2xl font-black">
              Tutorial: {{ currentTutorial.title }}
            </h3>
            <p class="text-orange-100 text-xs sm:text-sm mt-1">
              Aprende en 1 minuto cómo usar esta función en Realgestión.
            </p>
          </div>

          <!-- Selector de Pestañas del Tutorial -->
          <div class="flex border-b border-gray-200 bg-gray-50 px-6 pt-3 gap-2 overflow-x-auto">
            <button 
              v-for="tab in tutorialTabs" 
              :key="tab.id"
              type="button"
              class="py-2 px-3 text-xs font-bold rounded-t-xl transition-all whitespace-nowrap flex items-center gap-1.5 border-b-2"
              :class="selectedTutorialTab === tab.id 
                ? 'border-orange-600 bg-white text-orange-600 shadow-xs' 
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-100'"
              @click="selectedTutorialTab = tab.id"
            >
              <i :class="tab.icon" />
              <span>{{ tab.label }}</span>
            </button>
          </div>

          <!-- Contenido del Tutorial según la pestaña -->
          <div class="p-6 sm:p-8 max-h-[70vh] overflow-y-auto space-y-6">
            <!-- 1. Tutorial de Productos -->
            <div v-if="selectedTutorialTab === 'products'" class="space-y-5">
              <div class="bg-orange-50/60 p-4 rounded-2xl border border-orange-100 flex items-start gap-3">
                <i class="fas fa-info-circle text-orange-600 text-base mt-0.5" />
                <p class="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  Crear productos es el corazón de Realgestión. Cada producto te permite fijar precios, controlar existencias, calcular utilidades y venderlo en el POS o tu catálogo online.
                </p>
              </div>

              <div class="space-y-4">
                <div class="flex items-start gap-3.5">
                  <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-black text-xs shrink-0 mt-0.5">1</div>
                  <div>
                    <h4 class="font-bold text-sm text-gray-900">Ingresa a Productos &bull; Crear Producto</h4>
                    <p class="text-xs text-gray-600 mt-0.5">Haz clic en el botón naranja "+ Crear Producto" desde el menú o el acceso rápido.</p>
                  </div>
                </div>

                <div class="flex items-start gap-3.5">
                  <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-black text-xs shrink-0 mt-0.5">2</div>
                  <div>
                    <h4 class="font-bold text-sm text-gray-900">Completa Nombre, Código y Precio</h4>
                    <p class="text-xs text-gray-600 mt-0.5">Asigna el nombre del artículo, un código único (o código de barras) y el precio de venta al público.</p>
                  </div>
                </div>

                <div class="flex items-start gap-3.5">
                  <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-black text-xs shrink-0 mt-0.5">3</div>
                  <div>
                    <h4 class="font-bold text-sm text-gray-900">Foto, Costo y Categoría (Opcional)</h4>
                    <p class="text-xs text-gray-600 mt-0.5">Si agregas el costo, Realgestión calculará automáticamente tu margen de ganancia en cada venta.</p>
                  </div>
                </div>
              </div>

              <!-- Tip Box -->
              <div class="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <span class="font-bold">💡 Consejo Pro:</span> Si vendes servicios (consultorías, reparaciones, cursos) o no manejas stock físico, puedes crear el producto con costo cero o marcarlo sin control de depósito.
              </div>

              <!-- Enlace directo -->
              <div class="pt-2 flex justify-end">
                <Link 
                  href="/products/create" 
                  class="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs shadow transition-all"
                >
                  <i class="fas fa-plus" />
                  <span>Ir a Crear Producto Ahora</span>
                </Link>
              </div>
            </div>

            <!-- 2. Tutorial de Inventario -->
            <div v-if="selectedTutorialTab === 'inventory'" class="space-y-5">
              <div class="bg-orange-50/60 p-4 rounded-2xl border border-orange-100 flex items-start gap-3">
                <i class="fas fa-warehouse text-orange-600 text-base mt-0.5" />
                <p class="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  Tienes dos formas de cargar existencias a tus productos: ajuste directo de inventario (rápido) o registrar una compra a proveedores (con factura y costos).
                </p>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="p-4 rounded-2xl border border-gray-200 bg-white shadow-xs">
                  <div class="flex items-center gap-2 font-bold text-sm text-gray-900 mb-2">
                    <span class="w-6 h-6 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-xs">A</span>
                    <span>Ajuste de Inventario</span>
                  </div>
                  <p class="text-xs text-gray-600 leading-relaxed mb-3">
                    Ideal para comenzar con el stock que ya tienes en tienda. Vas a <strong>Inventario &bull; Agregar Existencia</strong> y cargas las cantidades directamente.
                  </p>
                  <Link href="/inventories" class="text-xs font-bold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
                    <span>Ir a Inventarios</span>
                    <i class="fas fa-arrow-right text-[10px]" />
                  </Link>
                </div>

                <div class="p-4 rounded-2xl border border-gray-200 bg-white shadow-xs">
                  <div class="flex items-center gap-2 font-bold text-sm text-gray-900 mb-2">
                    <span class="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs">B</span>
                    <span>Módulo de Compras</span>
                  </div>
                  <p class="text-xs text-gray-600 leading-relaxed mb-3">
                    Ideal cuando recibes mercancía de un proveedor. Registras la factura, el costo unitario pagado y las existencias se suman automáticamente.
                  </p>
                  <Link href="/shoppings/create" class="text-xs font-bold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
                    <span>Registrar Compra</span>
                    <i class="fas fa-arrow-right text-[10px]" />
                  </Link>
                </div>
              </div>

              <!-- Tip Box -->
              <div class="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <span class="font-bold">💡 Consejo Pro:</span> Puedes tener múltiples depósitos (almacén central, tienda, furgón). Todo el stock se descuenta en tiempo real al cobrar una venta.
              </div>
            </div>

            <!-- 3. Tutorial de Ventas y POS -->
            <div v-if="selectedTutorialTab === 'pos'" class="space-y-5">
              <div class="bg-orange-50/60 p-4 rounded-2xl border border-orange-100 flex items-start gap-3">
                <i class="fas fa-cash-register text-orange-600 text-base mt-0.5" />
                <p class="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  El Punto de Venta (POS) está optimizado para cobrar rápido: busca productos, escanea con pistola de código de barras y emite el comprobante en segundos.
                </p>
              </div>

              <div class="space-y-4">
                <div class="flex items-start gap-3.5">
                  <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-black text-xs shrink-0 mt-0.5">1</div>
                  <div>
                    <h4 class="font-bold text-sm text-gray-900">Abre el Punto de Venta (POS)</h4>
                    <p class="text-xs text-gray-600 mt-0.5">Diseñado para pantallas táctiles, computadoras o tablets. Accede desde el menú o el botón directo.</p>
                  </div>
                </div>

                <div class="flex items-start gap-3.5">
                  <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-black text-xs shrink-0 mt-0.5">2</div>
                  <div>
                    <h4 class="font-bold text-sm text-gray-900">Agrega productos al carrito</h4>
                    <p class="text-xs text-gray-600 mt-0.5">Haz clic en los artículos o utiliza el buscador rápido por nombre o código de barras.</p>
                  </div>
                </div>

                <div class="flex items-start gap-3.5">
                  <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-black text-xs shrink-0 mt-0.5">3</div>
                  <div>
                    <h4 class="font-bold text-sm text-gray-900">Selecciona Cliente y Cobrar</h4>
                    <p class="text-xs text-gray-600 mt-0.5">Elige el método de pago (Efectivo, Tarjeta, Transferencia, Pago Móvil, Dólares o Mixto) y emite el recibo.</p>
                  </div>
                </div>
              </div>

              <!-- Enlace directo -->
              <div class="pt-2 flex justify-end gap-3">
                <Link 
                  href="/orders/create" 
                  class="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2.5 px-4 rounded-xl text-xs transition-all"
                >
                  <span>Crear Venta / Pedido</span>
                </Link>
                <Link 
                  href="/pos" 
                  class="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs shadow transition-all"
                >
                  <i class="fas fa-cash-register" />
                  <span>Abrir Punto de Venta (POS)</span>
                </Link>
              </div>
            </div>
          </div>

          <!-- Pie del Modal -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
            <button 
              type="button" 
              class="px-5 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold text-xs transition-colors"
              @click="showTutorialModal = false"
            >
              Cerrar Tutorial
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'

export default {
  components: {
    Link,
  },
  props: {
    onboarding: Object,
    account: Object,
  },
  data() {
    return {
      isCollapsed: false,
      isPermanentlyDismissed: false,
      showTutorialModal: false,
      selectedTutorialTab: 'products',
      tutorialTabs: [
        { id: 'products', label: '1. Productos y Servicios', icon: 'fas fa-box-open' },
        { id: 'inventory', label: '2. Inventario y Compras', icon: 'fas fa-warehouse' },
        { id: 'pos', label: '3. Punto de Venta (POS)', icon: 'fas fa-cash-register' },
      ],
    }
  },
  computed: {
    storageKey() {
      const id = this.account?.id || 'default'
      return `realgestion_onboarding_dismissed_${id}`
    },
    collapseKey() {
      const id = this.account?.id || 'default'
      return `realgestion_onboarding_collapsed_${id}`
    },
    currentTutorial() {
      const found = this.tutorialTabs.find(t => t.id === this.selectedTutorialTab)
      return found || this.tutorialTabs[0]
    },
  },
  watch: {
    isCollapsed(newVal) {
      localStorage.setItem(this.collapseKey, newVal ? 'true' : 'false')
    },
  },
  mounted() {
    // Si la guía está completada y el usuario la cerró anteriormente, respetamos la preferencia
    if (this.onboarding?.is_completed) {
      const dismissed = localStorage.getItem(this.storageKey)
      if (dismissed === 'true') {
        this.isPermanentlyDismissed = true
      }
    }
    const collapsed = localStorage.getItem(this.collapseKey)
    if (collapsed === 'true') {
      this.isCollapsed = true
    }
  },
  methods: {
    openTutorial(tutorialId) {
      if (['products', 'inventory', 'pos'].includes(tutorialId)) {
        this.selectedTutorialTab = tutorialId
      }
      this.showTutorialModal = true
    },
    dismissPermanently() {
      this.isPermanentlyDismissed = true
      localStorage.setItem(this.storageKey, 'true')
    },
  },
}
</script>
